import React, { useEffect, useState, useCallback } from "react";
import { api, fmtBRL, fmtDate, formatError } from "@/lib/api";
import { SectionTitle, Badge, Btn, Modal, SelectField, Field, TextArea, EmptyState } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, ArrowDownToLine, ArrowUpFromLine, Trash2, Filter, TrendingUp, TrendingDown, Wallet } from "lucide-react";

const empty = { product_id: "", tipo: "entrada", quantidade: 1, valor_unitario: "", motivo: "" };

export default function Movements() {
  const { hasRole } = useAuth();
  const canDelete = hasRole("admin", "almoxarife");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_entradas: 0, total_saidas: 0, saldo: 0, count_entradas: 0, count_saidas: 0 });
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(""); // "" | today | month | year
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (period) params.period = period;
    if (customStart) params.start = customStart;
    if (customEnd) params.end = customEnd;
    const [m, s, p] = await Promise.all([
      api.get("/movements"),
      api.get("/movements/summary", { params }),
      products.length ? Promise.resolve({ data: products }) : api.get("/products"),
    ]);
    setItems(m.data); setSummary(s.data);
    if (!products.length) setProducts(p.data);
    setLoading(false);
  }, [period, customStart, customEnd, products]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, customStart, customEnd]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        product_id: form.product_id,
        tipo: form.tipo,
        quantidade: parseFloat(form.quantidade),
        motivo: form.motivo,
      };
      if (form.valor_unitario !== "" && form.valor_unitario !== null) {
        payload.valor_unitario = parseFloat(form.valor_unitario) || 0;
      }
      await api.post("/movements", payload);
      toast.success("Movimentação registrada");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (m) => {
    const choice = window.prompt(
      `Excluir movimentação de ${m.quantidade} × ${m.product_nome}?\n\n1 — Excluir E ESTORNAR estoque\n2 — Só excluir o registro (limpar teste)\n\nDigite 1 ou 2:`,
      "1"
    );
    if (choice === null) return;
    const reverse = choice.trim() === "1";
    if (choice.trim() !== "1" && choice.trim() !== "2") { toast.error("Opção inválida"); return; }
    try {
      await api.delete(`/movements/${m.id}?reverse_stock=${reverse}`);
      toast.success(reverse ? "Movimentação excluída · estoque estornado" : "Registro excluído (estoque intocado)");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="movements-page">
      <SectionTitle action={<Btn onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-movement-btn"><Plus className="w-4 h-4" />Nova Movimentação</Btn>}>
        Entradas e Saídas
      </SectionTitle>

      {/* Cards de Resumo Financeiro */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="brutal-card p-4 bg-[#F0F7F2]" data-testid="card-entradas">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-[#1A7F37]" />
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57]">Total Entradas</div>
          </div>
          <div className="font-display font-black text-3xl text-[#1A7F37]">{fmtBRL(summary.total_entradas)}</div>
          <div className="font-mono-data text-[10px] text-[#777] mt-1">{summary.count_entradas} movimentações</div>
        </div>
        <div className="brutal-card p-4 bg-[#FFF3ED]" data-testid="card-saidas">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-[#FF4F00]" />
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#B33A00]">Total Saídas</div>
          </div>
          <div className="font-display font-black text-3xl text-[#FF4F00]">{fmtBRL(summary.total_saidas)}</div>
          <div className="font-mono-data text-[10px] text-[#777] mt-1">{summary.count_saidas} movimentações</div>
        </div>
        <div className="brutal-card p-4 bg-[#0A1F14] text-white" data-testid="card-saldo">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-[#FF9F5A]" />
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-white/70">Saldo Atual</div>
          </div>
          <div className={`font-display font-black text-3xl ${summary.saldo >= 0 ? "text-[#7FE39F]" : "text-[#FF6B6B]"}`}>{fmtBRL(summary.saldo)}</div>
          <div className="font-mono-data text-[10px] text-white/60 mt-1">Entradas − Saídas</div>
        </div>
      </div>

      {/* Filtros por período */}
      <div className="brutal-card-sm flex flex-wrap items-center gap-2 px-3 py-2 mb-4 bg-white" data-testid="period-filter">
        <Filter className="w-4 h-4 text-[#555]" />
        <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mr-1">Período:</span>
        {[
          { v: "", label: "Todos" },
          { v: "today", label: "Hoje" },
          { v: "month", label: "Este mês" },
          { v: "year", label: "Este ano" },
        ].map((p) => (
          <button
            key={p.v || "all"}
            onClick={() => { setPeriod(p.v); setCustomStart(""); setCustomEnd(""); }}
            data-testid={`period-${p.v || "all"}`}
            className={`px-3 py-1 border-2 border-[#111] text-xs font-bold uppercase ${period === p.v && !customStart && !customEnd ? "bg-[#111] text-white" : "bg-white"}`}
          >
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <span className="font-mono-data text-[10px] text-[#555]">De</span>
          <input type="date" value={customStart} onChange={(e) => { setCustomStart(e.target.value); setPeriod(""); }} className="border-2 border-[#111] p-1 text-xs" data-testid="filter-start" />
          <span className="font-mono-data text-[10px] text-[#555]">até</span>
          <input type="date" value={customEnd} onChange={(e) => { setCustomEnd(e.target.value); setPeriod(""); }} className="border-2 border-[#111] p-1 text-xs" data-testid="filter-end" />
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Tipo</th>
              <th>Código</th>
              <th>Produto</th>
              <th className="text-right">Qtd</th>
              <th className="text-right">Vlr Un</th>
              <th className="text-right">Vlr Total</th>
              <th>Motivo</th>
              <th>Responsável</th>
              {canDelete && <th className="w-16">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={canDelete ? 10 : 9} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && items.length === 0 && <EmptyState message="Nenhuma movimentação ainda." cols={canDelete ? 10 : 9} />}
            {items.map((m) => {
              const vt = m.valor_total || (m.valor_unitario && m.quantidade ? m.valor_unitario * m.quantidade : 0);
              return (
                <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                  <td className="font-mono-data text-xs">{fmtDate(m.created_at)}</td>
                  <td>{m.tipo === "entrada" ? <Badge variant="success"><ArrowDownToLine className="w-3 h-3 inline mr-1" />Entrada</Badge> : <Badge variant="orange"><ArrowUpFromLine className="w-3 h-3 inline mr-1" />Saída</Badge>}</td>
                  <td className="font-mono-data text-xs">{m.product_codigo}</td>
                  <td>{m.product_nome}</td>
                  <td className="text-right font-mono-data font-bold">{m.quantidade}</td>
                  <td className="text-right font-mono-data text-xs">{fmtBRL(m.valor_unitario || 0)}</td>
                  <td className={`text-right font-mono-data font-bold ${m.tipo === "entrada" ? "text-[#1A7F37]" : "text-[#FF4F00]"}`}>{fmtBRL(vt)}</td>
                  <td className="text-sm text-[#555]">{m.motivo || "—"}</td>
                  <td className="text-sm text-[#555]">{m.responsavel_nome || "—"}</td>
                  {canDelete && (
                    <td>
                      <button onClick={() => remove(m)} data-testid={`delete-movement-${m.id}`} title="Excluir movimentação" className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nova Movimentação" size="md">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="movement-form">
          <SelectField label="Produto *" required value={form.product_id} onChange={(v) => {
            const prod = products.find((p) => p.id === v);
            setForm({ ...form, product_id: v, valor_unitario: prod ? String(prod.preco_custo || "") : "" });
          }} options={products.map((p) => ({ value: p.id, label: `${p.codigo} · ${p.nome} (atual: ${p.estoque_atual})` }))} colspan />
          <SelectField label="Tipo *" required value={form.tipo} onChange={(v) => setForm({ ...form, tipo: v })}
            options={[{value:"entrada",label:"Entrada"},{value:"saida",label:"Saída"}]} />
          <Field label="Quantidade *" type="number" required value={form.quantidade} onChange={(v) => setForm({ ...form, quantidade: v })} />
          <Field label="Valor Unitário (R$)" type="number" value={form.valor_unitario} onChange={(v) => setForm({ ...form, valor_unitario: v })} placeholder="Herda do produto se vazio" colspan testid="mov-valor-unitario" />
          <TextArea label="Motivo" value={form.motivo} onChange={(v) => setForm({ ...form, motivo: v })} />
          <div className="col-span-2 bg-[#FAFAFA] border-2 border-[#111] p-3 flex justify-between items-center">
            <span className="font-mono-data text-xs text-[#555]">Valor total estimado</span>
            <span className="font-display font-black text-xl">{fmtBRL((parseFloat(form.quantidade) || 0) * (parseFloat(form.valor_unitario) || 0))}</span>
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-mov-btn">Registrar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
